import { useReactiveVar } from "@apollo/client";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Nav,
  Row,
  Spinner,
  Tab,
} from "react-bootstrap";
import { ExclamationCircle } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Wrapper from "../../components/wrapper";
import {
  FAILED_DOMAIN_STATUSES,
  SUCCESS_DOMAIN_STATUSES,
  VERIFYING_DOMAIN_STATUSES,
} from "../../constants";
import {
  isDisabledWhiteLabelProjectTabs,
  whiteLabelJRStartModal,
} from "../../qraphql/globalVar/state";
import {
  useOrganizerWhiteLabelQuery,
  useRetryWhiteLabelDomainSetupMutation,
} from "../../qraphql/whiteLabel/whiteLabel.hooks";
import GeneralInfo from "./generalInfo";
import WhiteLabelCreatedModal from "./modals/WhiteLabelCreatedModal";
import WhiteLabelSettingsModal from "./modals/WhiteLabelSettingsModal";
import SeoSettings from "./seoSettings";
import SocialLinks from "./socialLinks";

enum ACTIVE_TAB {
  General = "General",
  Seo = "Seo",
  Links = "Links",
}

export default function ProjectSettings() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>(ACTIVE_TAB.General);
  const [isWhiteLabelCreatedModal, setIsWhiteLabelCreatedModal] =
    useState(false);

  const isDisabledTabs = useReactiveVar(isDisabledWhiteLabelProjectTabs);
  const whiteLabelJRStartModalValue = useReactiveVar(whiteLabelJRStartModal);

  const { data: whiteLabelInfo, refetch: refetchWhiteLabelInfo } =
    useOrganizerWhiteLabelQuery({
      onCompleted: (data) => {
        if (data.organizer?.whiteLabel?.projectName) {
          isDisabledWhiteLabelProjectTabs(false);
        }
      },
    });

  const [
    retryWhiteLabelDomainSetup,
    { loading: retryWhiteLabelDomainSetupLoading },
  ] = useRetryWhiteLabelDomainSetupMutation();

  useEffect(() => {
    if (searchParams.has("complete")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("complete");
      setIsWhiteLabelCreatedModal(true);
      navigate(
        {
          pathname: "/project-settings",
          search: newParams.toString(),
        },
        { replace: true }
      );
    }
  }, [searchParams, navigate]);

  const handleTrySetup = () => {
    if (retryWhiteLabelDomainSetupLoading) return;
    retryWhiteLabelDomainSetup({
      variables: { whiteLabelId: whiteLabelInfo.organizer?.whiteLabel?.id },
      onCompleted: () => {
        refetchWhiteLabelInfo();
      },
    });
  };

  useEffect(() => {
    if (whiteLabelJRStartModalValue) {
      (document.querySelector(".general-tab") as HTMLElement | null)?.click();
    }
  }, [whiteLabelJRStartModalValue]);

  return (
    <Wrapper mainBlockClassName="white-label-wrapper">
      <Container>
        {isWhiteLabelCreatedModal && (
          <WhiteLabelCreatedModal
            handleClose={() => setIsWhiteLabelCreatedModal(false)}
            projectLink={whiteLabelInfo?.organizer?.whiteLabel?.domain}
          />
        )}
        <WhiteLabelSettingsModal />
        <Row className="gy-3 align-items-center">
          <Col>
            <h2 className="fw-bold fs-1">{t("menu.Project Settings")}</h2>
          </Col>
          {whiteLabelInfo?.organizer?.whiteLabel?.domain && (
            <Col lg={4}>
              <Button
                as="a"
                target="_blank"
                href={`https://${whiteLabelInfo?.organizer?.whiteLabel?.domain}`}
                variant="success"
                className="p-3 px-2 w-100 ms-lg-auto d-block project-btn"
                disabled={
                  !SUCCESS_DOMAIN_STATUSES.includes(
                    whiteLabelInfo?.organizer?.whiteLabel?.domainStatus
                  )
                }
              >
                {t("whiteLabel.View Project")}
              </Button>
            </Col>
          )}
        </Row>
        {VERIFYING_DOMAIN_STATUSES.includes(
          whiteLabelInfo?.organizer?.whiteLabel?.domainStatus
        ) && (
          <Col>
            <div className="text-start text-warning mt-4 bg-warning-light2 p-3 rounded d-flex align-items-center gap-3">
              <div>
                <ExclamationCircle />
              </div>
              <div className="fs-7">
                {t(
                  "whiteLabel.Your domain is being verified. You can see the status of the domain in the sidebar custom domain."
                )}
              </div>
            </div>
          </Col>
        )}
        {FAILED_DOMAIN_STATUSES.includes(
          whiteLabelInfo?.organizer?.whiteLabel?.domainStatus
        ) && (
          <Col>
            <div className="text-start text-danger mt-3 bg-danger-light p-3 rounded d-flex align-items-center gap-3">
              <div>
                <ExclamationCircle />
              </div>
              <div className="fs-7">
                <div>
                  {t("whiteLabel.domainError")}{" "}
                  <span
                    className="text-danger text-decoration-underline cursor-pointer"
                    onClick={handleTrySetup}
                  >
                    {t("whiteLabel.try setup again")}
                  </span>
                  {retryWhiteLabelDomainSetupLoading && (
                    <Spinner animation="border" size="sm" className="ms-2" />
                  )}
                </div>
              </div>
            </div>
          </Col>
        )}

        <Tab.Container
          activeKey={activeTab}
          onSelect={(tab) => setActiveTab(tab)}
        >
          <Row className="mt-4">
            <Col>
              <Nav variant="tabs" className="w-100 mb-5 text-center">
                <Nav.Item
                  className={`col-4 d-flex justify-content-center align-items-center ${activeTab === ACTIVE_TAB.General && "tab-active-border text-success"}`}
                >
                  <Nav.Link
                    eventKey={ACTIVE_TAB.General}
                    className={`general-tab border-0 bg-transparent fw-bold fs-8 ${activeTab === ACTIVE_TAB.General ? "text-success" : "text-dark"}`}
                  >
                    {t("whiteLabel.General Information")}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item
                  className={`col-4 d-flex justify-content-center align-items-center ${activeTab === ACTIVE_TAB.Seo && "tab-active-border"}`}
                >
                  <Nav.Link
                    eventKey={ACTIVE_TAB.Seo}
                    className={`seo-settings-tab border-0 bg-transparent fw-bold fs-8 ${activeTab === ACTIVE_TAB.Seo ? "text-success" : "text-dark"} ${isDisabledTabs ? "opacity-50" : ""}`}
                    disabled={isDisabledTabs}
                  >
                    {t("whiteLabel.SEO Settings")}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item
                  className={`col-4 d-flex justify-content-center align-items-center ${activeTab === ACTIVE_TAB.Links && "tab-active-border"}`}
                >
                  <Nav.Link
                    eventKey={ACTIVE_TAB.Links}
                    className={`social-links-tab border-0 bg-transparent fw-bold fs-8 ${activeTab === ACTIVE_TAB.Links ? "text-success" : "text-dark"} ${isDisabledTabs ? "opacity-50" : ""}`}
                    disabled={isDisabledTabs}
                  >
                    {t("whiteLabel.Social Links")}
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content className="p-0">
                <Tab.Pane eventKey={ACTIVE_TAB.General}>
                  <GeneralInfo />
                </Tab.Pane>
                <Tab.Pane eventKey={ACTIVE_TAB.Seo}>
                  <SeoSettings />
                </Tab.Pane>
                <Tab.Pane eventKey={ACTIVE_TAB.Links}>
                  <SocialLinks />
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Container>
    </Wrapper>
  );
}
